/** biome-ignore-all lint/complexity/noExcessiveCognitiveComplexity: Complex SSE processing logic required */
/** biome-ignore-all lint/nursery/noUnnecessaryConditions: Type-safe conditional logic for server logs */

import { useEffect, useRef, useState } from "react";
import { useDebounceValue } from "usehooks-ts";
import { BFF_URL } from "@/lib/const";
import { sendRequest } from "@/lib/http-client";
import { useOrgProjectRouteContext } from "@/lib/org-project-context/hooks";
import {
  extractLogLevel,
  parseSSEDataLine,
  type SSEEvent,
} from "./log-transformers";

// Constants for SSE parsing
const DATA_PREFIX_LENGTH = 6; // Length of "data: "
const LOGS_DEBOUNCE_DELAY = 200;

// Helper function to create SSE request
const createSSERequest = (
  serverId: string,
  organizationId: string,
  signal: AbortSignal
) => {
  return sendRequest(
    new URL(
      `/api/coordinator/orgs/${organizationId}/workers/logs/${serverId}`,
      BFF_URL
    ),
    {
      signal,
      headers: {
        Accept: "text/event-stream",
        "Cache-Control": "no-cache",
      },
    }
  );
};

type UseGetServerLogsOptions = {
  enabled?: boolean;
};

export const useGetServerLogs = (
  serverId: string,
  options: UseGetServerLogsOptions = {}
) => {
  const { enabled = true } = options;
  const { organizationId } = useOrgProjectRouteContext();
  const [error, setError] = useState<Error | null>(null);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const [events, setEvents] = useState<SSEEvent[]>([]);
  const [data] = useDebounceValue(events, LOGS_DEBOUNCE_DELAY);

  useEffect(() => {
    if (!enabled) {
      setIsConnected(false);
      return;
    }

    const connect = async () => {
      try {
        const controller = new AbortController();
        abortControllerRef.current = controller;

        const response = await createSSERequest(
          serverId,
          organizationId,
          controller.signal
        );

        if (!(response.ok && response.body)) {
          throw new Error("Failed to establish SSE connection");
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder("utf-8");
        setIsConnected(true);

        let currentPosition = 0;
        setEvents([]);

        // eslint-disable-next-line no-constant-condition
        while (true) {
          const { done, value } = await reader.read();
          if (done) {
            break;
          }

          const chunk = decoder.decode(value);
          const lines = chunk.split("\n");

          for (const line of lines) {
            if (line.startsWith("data: ")) {
              const dataContent = line.substring(DATA_PREFIX_LENGTH).trim();
              if (dataContent) {
                const parsed = parseSSEDataLine(dataContent);
                if (parsed) {
                  const newEvent: SSEEvent = {
                    type: extractLogLevel(parsed.line),
                    data: parsed.line,
                    position: currentPosition,
                  };
                  currentPosition += 1;
                  setEvents((prev) => [...prev, newEvent]);
                }
              }
            }
          }
        }
      } catch (err) {
        if (err instanceof Error && err.name !== "AbortError") {
          setError(err);
          setIsConnected(false);
        }
      }
    };

    connect();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      setIsConnected(false);
    };
  }, [serverId, organizationId, enabled]);

  return { data, error, isConnected };
};
